/**
 * Catálogo de features/accesos que un usuario staff o viewer puede tener habilitados.
 *
 * Cada `id` es la clave que se guarda en el array `allowedFeatures` del documento
 * `users/{uid}` en Firestore. Mantener esta lista sincronizada con:
 *   - Las rutas protegidas en `App.js`
 *   - Los botones del `AdminPanel`
 *   - Las Cloud Functions callables que validan permisos del lado server.
 *
 * Los ids con uso conocido en el código:
 *   - localSales, adminCustomers  → AdminCustomersPage
 *   - stockManagement, suppliers, massEditor, discountConfig, salesHistory,
 *     salesMetrics, salesChart, qrScanner, qrList, freeTickets, adminEvents,
 *     subscribers, adminCustomers
 *       → rutas en App.js (ver ROUTE_PATH_TO_FEATURE).
 *
 * Mantener esta lista en sync con:
 *   - `ROUTE_PATH_TO_FEATURE` / `ADMIN_VIEW_TO_FEATURE` abajo.
 *   - `STAFF_FEATURE_IDS` en `functions/index.js` (sanitize server-side).
 */
export const STAFF_FEATURE_GROUPS = [
  {
    title: 'Ventas',
    items: [
      {
        id: 'localSales',
        label: 'Registrar ventas en local',
        description: 'Permite cargar ventas de mostrador y consultar esas ventas en Clientes.'
      },
      {
        id: 'salesHistory',
        label: 'Ver historial de ventas',
        description: 'Muestra el listado completo de tickets y operaciones vendidas.'
      },
      {
        id: 'salesMetrics',
        label: 'Ver métricas de ventas',
        description: 'Habilita reportes, KPIs e indicadores de ventas.'
      },
      {
        id: 'salesChart',
        label: 'Ver gráfico de ventas',
        description: 'Habilita la visualización estilo Wall Street del rendimiento de ventas.'
      }
    ]
  },
  {
    title: 'Catálogo y stock',
    items: [
      {
        id: 'stockManagement',
        label: 'Gestionar inventario',
        description: 'Permite ver y actualizar stock, costos y reposiciones de productos.'
      },
      {
        id: 'suppliers',
        label: 'Gestionar proveedores',
        description: 'Habilita la base de proveedores, compras registradas y deudas.'
      },
      {
        id: 'massEditor',
        label: 'Editar productos en lote',
        description: 'Permite modificar precios, textos y datos de varios productos a la vez.'
      },
      {
        id: 'discountConfig',
        label: 'Configurar descuentos',
        description: 'Permite crear y editar promociones, cupones y reglas de descuento.'
      }
    ]
  },
  {
    title: 'Clientes y contactos',
    items: [
      {
        id: 'adminCustomers',
        label: 'Gestionar clientes',
        description: 'Habilita fichas de clientes, datos fiscales e historial asociado.'
      },
      {
        id: 'subscribers',
        label: 'Ver suscriptores',
        description: 'Permite consultar la lista de altas al newsletter.'
      }
    ]
  },
  {
    title: 'QR y accesos',
    items: [
      {
        id: 'qrScanner',
        label: 'Escanear entradas QR',
        description: 'Permite validar entradas en puerta con el scanner.'
      },
      {
        id: 'qrList',
        label: 'Ver ingresos escaneados',
        description: 'Muestra el listado de QR validados y accesos gratuitos usados.'
      },
      {
        id: 'freeTickets',
        label: 'Crear entradas gratis',
        description: 'Permite generar cortesías, invitaciones y tickets sin cargo.'
      },
      {
        id: 'adminEvents',
        label: 'Gestionar eventos',
        description: 'Permite crear, activar y revisar eventos asociados a entradas.'
      }
    ]
  }
];

/** Todos los ids válidos aplanados. Útil para validar entradas externas. */
export const ALL_STAFF_FEATURE_IDS = STAFF_FEATURE_GROUPS.flatMap((g) =>
  g.items.map((i) => i.id)
);

/**
 * Mapeo de subvistas internas usadas como ?view= / tab-key → featureId.
 * Las subvistas internas de clientes comparten el permiso general de Clientes.
 * Si agregás una pestaña nueva, registrala acá.
 */
export const ADMIN_VIEW_TO_FEATURE = {
  sales: 'adminCustomers',
  customers: 'adminCustomers',
  subscribers: 'subscribers'
};

/**
 * Mapeo de ruta pública (pathname exacto) al featureId requerido para acceder.
 * Las rutas que NO están acá son consideradas "solo admin" o públicas.
 * `/admin` se controla aparte vía canAccessAdminShell().
 */
export const ROUTE_PATH_TO_FEATURE = {
  '/local': 'localSales',
  '/gestion-stock': 'stockManagement',
  '/subscriptores': 'subscribers',
  '/editor-masivo': 'massEditor',
  '/configuracion-descuentos': 'discountConfig',
  '/historial-ventas': 'salesHistory',
  '/metricas': 'salesMetrics',
  '/grafico': 'salesChart',
  '/free': 'freeTickets',
  '/escanear-qr': 'qrScanner',
  '/qr-lista': 'qrList',
  '/admin/clientes': 'adminCustomers',
  '/admin/proveedores': 'suppliers',
  '/admin/eventos': 'adminEvents'
};

/**
 * Features "online" (pestañas y fichas dentro del shell admin).
 * Si hay al menos una habilitada, mostramos la sección online en el panel.
 */
const ONLINE_FEATURE_IDS = ['adminCustomers', 'subscribers'];

/**
 * Features "tool" del panel (botones a rutas sueltas fuera de la sección online).
 * Se derivan de todas las features menos las online.
 */
const TOOL_FEATURE_IDS = ALL_STAFF_FEATURE_IDS.filter(
  (id) => !ONLINE_FEATURE_IDS.includes(id)
);

const ROLE_IMPLIED_FEATURES = {
  cashier: ['localSales'],
  qrscanner: ['qrScanner']
};

function normalizeRole(role) {
  return typeof role === 'string' ? role.trim().toLowerCase() : '';
}

export function getEffectiveStaffFeatureIds(role, featureIds) {
  const explicitFeatures = Array.isArray(featureIds) ? featureIds : [];
  const impliedFeatures = ROLE_IMPLIED_FEATURES[normalizeRole(role)] || [];
  return [...new Set([...explicitFeatures, ...impliedFeatures])];
}

/**
 * Normaliza los datos que vienen de users/{uid} en Firestore al formato
 * canónico { role, features } que consume el resto de la app.
 */
export function normalizeStaffFromFirestore(userData) {
  const rawRole = userData?.role;
  const role =
    typeof rawRole === 'string'
      ? rawRole.trim().toLowerCase()
      : rawRole != null
        ? String(rawRole).trim().toLowerCase()
        : '';
  const features = Array.isArray(userData?.allowedFeatures)
    ? userData.allowedFeatures.filter((x) => typeof x === 'string')
    : [];
  return { role, features };
}

/**
 * Decide a qué ruta mandar al usuario tras un login exitoso, según su rol y
 * features habilitadas. Admin → /admin. Staff/viewer → primera ruta disponible.
 * Cashier / qrScanner mantienen su flow legacy (LocalSales / escáner).
 */
export function pickPostLoginPath(role, featureIds) {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole === 'admin') return '/admin';

  const set = new Set(getEffectiveStaffFeatureIds(role, featureIds));
  if (set.size === 0) return '/login';

  const order = [
    { path: '/admin', check: () => ONLINE_FEATURE_IDS.some((id) => set.has(id)) || TOOL_FEATURE_IDS.some((id) => set.has(id)) },
    { path: '/local', check: () => set.has('localSales') },
    { path: '/gestion-stock', check: () => set.has('stockManagement') },
    { path: '/admin/proveedores', check: () => set.has('suppliers') },
    { path: '/admin/clientes', check: () => set.has('adminCustomers') },
    { path: '/subscriptores', check: () => set.has('subscribers') },
    { path: '/editor-masivo', check: () => set.has('massEditor') },
    { path: '/historial-ventas', check: () => set.has('salesHistory') },
    { path: '/metricas', check: () => set.has('salesMetrics') },
    { path: '/grafico', check: () => set.has('salesChart') },
    { path: '/configuracion-descuentos', check: () => set.has('discountConfig') },
    { path: '/free', check: () => set.has('freeTickets') },
    { path: '/admin/eventos', check: () => set.has('adminEvents') },
    { path: '/escanear-qr', check: () => set.has('qrScanner') },
    { path: '/qr-lista', check: () => set.has('qrList') }
  ];
  for (const { path, check } of order) {
    if (check()) return path;
  }
  return '/';
}

/**
 * True si el usuario puede ver el shell /admin. Admin siempre. Staff/viewer
 * solo si tienen al menos una feature del panel (online o tool).
 */
export function canAccessAdminShellFromFeatures(isFullAdmin, featureIds) {
  if (isFullAdmin) return true;
  const set = new Set(Array.isArray(featureIds) ? featureIds : []);
  return (
    ONLINE_FEATURE_IDS.some((id) => set.has(id)) ||
    TOOL_FEATURE_IDS.some((id) => set.has(id))
  );
}

export function canAccessAdminShell(isFullAdmin, role, featureIds) {
  return canAccessAdminShellFromFeatures(
    isFullAdmin,
    getEffectiveStaffFeatureIds(role, featureIds)
  );
}

/** True si hay que mostrar la grilla de herramientas sueltas en el panel. */
export function showToolsGridFromFeatures(isFullAdmin, featureIds) {
  if (isFullAdmin) return true;
  const set = new Set(Array.isArray(featureIds) ? featureIds : []);
  return TOOL_FEATURE_IDS.some((id) => set.has(id));
}

/** True si hay que mostrar la sección de clientes y contactos. */
export function showOnlineSectionFromFeatures(isFullAdmin, featureIds) {
  if (isFullAdmin) return true;
  const set = new Set(Array.isArray(featureIds) ? featureIds : []);
  return ONLINE_FEATURE_IDS.some((id) => set.has(id));
}

/** Primera subvista del AdminDashboard a la que puede entrar el usuario. */
export function firstAllowedAdminSubview(featureIds) {
  const set = new Set(Array.isArray(featureIds) ? featureIds : []);
  const order = [
    ['sales', 'adminCustomers'],
    ['customers', 'adminCustomers'],
    ['subscribers', 'subscribers']
  ];
  for (const [view, fid] of order) {
    if (set.has(fid)) return view;
  }
  return '';
}
